import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";
import { mergeExistingFields } from "helpers/mergeExistingFields";
import { ApiErrorPayload } from "types/ApiErrorPayload";
import {
  getChangedFields,
  normalizeOptionalText,
} from "helpers/getChangedFields";
import { normalizeSeoHtml } from "helpers/seoAttributes";

// #region types
export interface MainText {
  id: number;
  title: string;
  geo: string;
  isActive: boolean;
  translations: Record<string, string>;
}

export interface MainTextExtended extends MainText {
  content: string;
}

interface DraftMainTextItem extends Omit<MainTextExtended, "id"> {
  id?: MainText["id"];
  isModified: boolean;
}

interface MainTextState {
  list: MainText[];
  selectedItems: MainText["id"][];
  isListFetched: boolean;
  isItemFetched: boolean;
  isItemFetching: boolean;
  activePage: number;
  totalItems: number;
  totalPages: number;
  processingItemId?: MainText["id"];
  draftItem: DraftMainTextItem;
  initialDraftItem?: DraftMainTextItem;
}

interface GetMainTextListPayload {
  limit?: number;
  offset?: number;
  title?: string;
  countries?: string[];
  isActive?: number;
  order?: Partial<Record<keyof Omit<MainText, "content">, string>>;
}

interface GetPartnersListResponse {
  items: MainText[];
  totalItems: number;
  totalPages: number;
}

type createMainTextItemPayload = Omit<
  MainTextExtended,
  "id" | "isActive" | "translations" | "geo"
> & { country: string; isActive: number };
type updateMainTextItemPayload = Omit<
  MainTextExtended,
  "isActive" | "translations" | "geo"
> & {
  country: string;
  isActive: number;
  translations?: Record<string, string>;
};

// #endregion types
const createDraftItem = (data?: MainTextExtended): DraftMainTextItem => {
  if (!data) {
    return {
      id: undefined,
      title: "",
      isActive: false,
      geo: "",
      content: "",
      translations: {},
      isModified: false,
    };
  }

  const { id, title, isActive, content, geo, translations } = data;
  const fixedTranslations =
    translations &&
    typeof translations === "object" &&
    !Array.isArray(translations)
      ? translations
      : {};

  return {
    id,
    title,
    isActive,
    geo,
    content,
    translations: fixedTranslations,
    isModified: false,
  };
};

const initialState: MainTextState = {
  list: [],
  selectedItems: [],
  isListFetched: false,
  isItemFetched: false,
  isItemFetching: false,
  activePage: 1,
  totalItems: 0,
  totalPages: 1,
  draftItem: createDraftItem(),
};

interface MainTextBunchUpdatePayload {
  ids: number[];
  isActive: number;
}

const bunchUpdateMainText = createAsyncThunk(
  "cms-main-texts/bunch-update",
  async (data: MainTextBunchUpdatePayload) => {
    const result = await instance.put(apiRoutes.mainTextBunchUpdate, data);

    return result.data;
  },
);

const getMainTextList = createAsyncThunk(
  "cms-main-texts/list",
  async (data: GetMainTextListPayload) => {
    try {
      const result = await instance.post(`${apiRoutes.mainTextList}`, data);
      return result.data as GetPartnersListResponse;
    } catch (error) {
      throw error;
    }
  },
);

const deleteMainTextItem = createAsyncThunk(
  "cms-main-texts/delete",
  async (id: number) => {
    try {
      await instance.delete(`${apiRoutes.mainTextDelete}/${id}`);
      return true;
    } catch (error) {
      throw error;
    }
  },
);

const getMainTextItem = createAsyncThunk(
  "cms-main-texts/item",
  async (id: number) => {
    try {
      const result = await instance.get(`${apiRoutes.mainTextItem}/${id}`);
      return result.data as MainTextExtended;
    } catch (error) {
      throw error;
    }
  },
);

const createMainTextItem = createAsyncThunk<
  MainTextExtended,
  createMainTextItemPayload,
  { rejectValue: ApiErrorPayload }
>(
  "cms-main-texts/create",
  async (data: createMainTextItemPayload, { rejectWithValue }) => {
    try {
      const result = await instance.post(`${apiRoutes.mainTextCreate}`, data);

      return result.data as MainTextExtended;
    } catch (error: any) {
      const apiError = error?.data?.error;
      if (apiError?.code) {
        return rejectWithValue(apiError);
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);

const updateMainTextItem = createAsyncThunk<
  MainTextExtended,
  updateMainTextItemPayload,
  { rejectValue: ApiErrorPayload }
>(
  "cms-main-texts/update",
  async (data: updateMainTextItemPayload, { rejectWithValue, getState }) => {
    try {
      const state = getState() as
        | MainTextState
        | { "cms-main-texts": MainTextState };
      const initialDraftItem =
        "cms-main-texts" in state
          ? state["cms-main-texts"].initialDraftItem
          : state.initialDraftItem;
      const currentFields = {
        title: data.title,
        content: data.content,
        isActive: data.isActive,
      };
      const initialFields = initialDraftItem
        ? {
            title: initialDraftItem.title,
            content: initialDraftItem.content,
            isActive: initialDraftItem.isActive ? 1 : 0,
          }
        : undefined;
      const changes = getChangedFields(
        initialFields,
        currentFields,
        ["title", "content", "isActive"],
        {
          title: normalizeOptionalText,
          content: normalizeSeoHtml,
          isActive: (value) => Boolean(value),
        },
      );
      const result = await instance.put(
        `${apiRoutes.mainTextUpdate}/${data.id}`,
        { country: data.country, ...changes },
      );

      return result.data as MainTextExtended;
    } catch (error: any) {
      const data = error?.data;
      const apiError = data?.error ?? data;

      if (apiError && apiError.code) {
        return rejectWithValue(apiError);
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);

const mainTextsSlice = createSlice({
  name: "cms-main-texts",
  initialState,
  reducers: {
    handleSelect: (state, action: PayloadAction<number>) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (el) => el !== action.payload,
        );
      } else {
        state.selectedItems.push(action.payload);
      }
    },
    handleSelectAll: (state) => {
      state.selectedItems =
        state.selectedItems.length !== state.list.length
          ? state.list.map((item) => item.id)
          : [];
    },
    handlePaginate: (state, action: PayloadAction<number>) => {
      state.activePage = action.payload;
      state.isListFetched = false;
      state.selectedItems = [];
    },
    handleClearDraftItem: (state) => {
      state.draftItem = createDraftItem();
      state.initialDraftItem = undefined;
      state.isItemFetched = false;
    },
    handleUpdateDraftItem: (
      state,
      action: PayloadAction<Partial<DraftMainTextItem>>,
    ) => {
      state.draftItem = {
        ...state.draftItem,
        ...action.payload,
        isModified: true,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getMainTextList.fulfilled, (state, action) => {
      state.list = action.payload.items;
      state.totalItems = action.payload.totalItems;
      state.totalPages = action.payload.totalPages;
      state.isListFetched = true;
    });
    builder.addCase(deleteMainTextItem.pending, (state, action) => {
      state.processingItemId = action.meta.arg;
    });
    builder.addCase(deleteMainTextItem.fulfilled, (state) => {
      state.processingItemId = undefined;
      state.isListFetched = false;
    });
    builder.addCase(deleteMainTextItem.rejected, (state) => {
      state.processingItemId = undefined;
    });
    builder.addCase(getMainTextItem.fulfilled, (state, action) => {
      state.isItemFetched = true;
      const draftItem = createDraftItem(action.payload);
      state.draftItem = draftItem;
      state.initialDraftItem = {
        ...draftItem,
        translations: { ...draftItem.translations },
      };
    });
    builder.addCase(createMainTextItem.pending, (state) => {
      state.isItemFetching = true;
    });
    builder.addCase(createMainTextItem.fulfilled, (state) => {
      state.isListFetched = false;
      state.isItemFetching = false;
    });
    builder.addCase(createMainTextItem.rejected, (state) => {
      state.isItemFetching = false;
    });
    builder.addCase(updateMainTextItem.pending, (state, action) => {
      state.isItemFetching = true;
      state.processingItemId = action.meta.arg.id;
    });
    builder.addCase(updateMainTextItem.fulfilled, (state, action) => {
      state.isItemFetching = false;
      state.list = state.list.map((item) =>
        item.id === action.payload.id
          ? mergeExistingFields(item, action.payload)
          : item,
      );
      state.processingItemId = undefined;
      const draftItem = createDraftItem(action.payload);
      state.draftItem = draftItem;
      state.initialDraftItem = {
        ...draftItem,
        translations: { ...draftItem.translations },
      };
    });
    builder.addCase(updateMainTextItem.rejected, (state) => {
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(bunchUpdateMainText.fulfilled, (state) => {
      state.isListFetched = false;
      state.selectedItems = [];
    });
  },
});

const {
  handleSelect,
  handleSelectAll,
  handlePaginate,
  handleClearDraftItem,
  handleUpdateDraftItem,
} = mainTextsSlice.actions;

export {
  handleSelect,
  handleSelectAll,
  handlePaginate,
  getMainTextList,
  mainTextsSlice,
  deleteMainTextItem,
  getMainTextItem,
  handleClearDraftItem,
  handleUpdateDraftItem,
  updateMainTextItem,
  createMainTextItem,
  bunchUpdateMainText,
};
